import React, { useState, useEffect, useRef } from 'react';
import { Search, Bell, Plus, User, Settings } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { patientsAPI } from '@/services/api';
import { Patient } from '@/types/patient';
import { usePatientSearchConfig } from '@/hooks/usePatientSearchConfig';
import { FollowPatientModal } from '@/components/patient-record/FollowPatientModal';
import { usePatientTabs } from '@/hooks/usePatientTabs';
import { Checkbox } from '@/components/ui/Checkbox';
import { cn } from '@/utils/cn';

export const Header: React.FC = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showConfigMenu, setShowConfigMenu] = useState(false);
  const [activeResultIndex, setActiveResultIndex] = useState(-1);
  const [isFollowModalOpen, setIsFollowModalOpen] = useState(false);
  const [followPatientId, setFollowPatientId] = useState<string | number | null>(null);
  const [followPatientName, setFollowPatientName] = useState<string>('');
  const searchContainerRef = useRef<HTMLDivElement>(null);
  const configMenuRef = useRef<HTMLDivElement>(null);
  const { config, updateConfig } = usePatientSearchConfig();
  const { openPatient } = usePatientTabs();

  // Search patients
  const { data: searchResults, isLoading: isSearching } = useQuery({
    queryKey: ['patientSearch', searchQuery],
    queryFn: async () => {
      if (!searchQuery.trim()) {
        return [];
      }
      const response = await patientsAPI.getAll({
        search: searchQuery,
        page: 1,
        pageSize: 8, // Limit to 8 results for dropdown
      });
      if (response.data?.success) {
        return response.data.data?.data || [];
      }
      return response.data?.data || [];
    },
    enabled: searchQuery.trim().length > 0,
    staleTime: 30000, // Cache for 30 seconds
  });

  const results = (searchResults || []) as Patient[];

  // Format date for display
  const formatDate = (dateString?: string) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch {
      return dateString;
    }
  };

  // Format phone number
  const formatPhone = (phone?: string) => {
    if (!phone) return '';
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }
    return phone;
  };

  // Build meta text for patient result
  const buildMetaText = (patient: Patient) => {
    const parts: string[] = [];
    if (config.fields.showMRN && patient.mrn) {
      parts.push(`MRN ${patient.mrn}`);
    }
    if (config.fields.showDOB && patient.dob) {
      parts.push(formatDate(patient.dob));
    }
    if (config.fields.showPhone && patient.mobile) {
      parts.push(formatPhone(patient.mobile));
    }
    return parts.join(' • ');
  };

  // Handle search input change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);
    setShowSearchResults(value.length > 0);
    setActiveResultIndex(-1);
  };

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showSearchResults || results.length === 0) {
      if (e.key === 'Escape') {
        setShowSearchResults(false);
        setShowConfigMenu(false);
      }
      return;
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveResultIndex((prev) => (prev < results.length - 1 ? prev + 1 : prev));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveResultIndex((prev) => (prev > 0 ? prev - 1 : -1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (activeResultIndex >= 0 && activeResultIndex < results.length) {
        handlePatientClick(results[activeResultIndex]);
      }
    } else if (e.key === 'Escape') {
      setShowSearchResults(false);
      setShowConfigMenu(false);
      setSearchQuery('');
    }
  };

  // Handle patient click - opens in tabbed view
  const handlePatientClick = async (patient: Patient) => {
    if (!patient?.id) return;
    
    // If we don't have full patient data, fetch it
    let fullPatient = patient;
    if (!patient.firstName && !patient.lastName) {
      try {
        const response = await patientsAPI.getById(patient.id);
        if (response.data?.success && response.data.data) {
          fullPatient = response.data.data;
        } else if (response.data?.data) {
          fullPatient = response.data.data;
        } else if (response.data) {
          fullPatient = response.data;
        }
      } catch (error) {
        console.error('Failed to fetch patient:', error);
        return;
      }
    }
    
    // Open patient in tab and navigate to patients page
    openPatient(fullPatient);
    setShowSearchResults(false);
    setSearchQuery('');
    navigate('/patients');
  };

  // Handle action button click
  const handleActionClick = async (e: React.MouseEvent, action: string, patient: Patient) => {
    e.stopPropagation();
    if (!patient?.id) return;

    switch (action) {
      case 'follow':
        setFollowPatientId(patient.id);
        setFollowPatientName(`${patient.firstName || ''} ${patient.lastName || ''}`.trim() || 'Patient');
        setIsFollowModalOpen(true);
        setShowSearchResults(false);
        break;
      case 'view':
        // Open in standalone view
        setShowSearchResults(false);
        setSearchQuery('');
        navigate(`/patients/${patient.id}`);
        break;
      case 'video':
        // TODO: Implement video visit
        console.log('Video visit for patient:', patient.id);
        break;
      case 'message':
        // TODO: Navigate to messages
        navigate('/messages');
        break;
      case 'orderDevice':
        // TODO: Implement device ordering
        console.log('Order device for patient:', patient.id);
        break;
    }
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        searchContainerRef.current &&
        !searchContainerRef.current.contains(event.target as Node)
      ) {
        setShowSearchResults(false);
      }
      if (
        configMenuRef.current &&
        !configMenuRef.current.contains(event.target as Node)
      ) {
        setShowConfigMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Get enabled actions
  const enabledActions = [
    { id: 'view', label: 'View' },
    { id: 'video', label: 'Video Visit' },
    { id: 'message', label: 'Message Patient' },
    { id: 'orderDevice', label: 'Order Device' },
    { id: 'follow', label: 'Follow' },
  ].filter((action) => config.actions[action.id as keyof typeof config.actions]);

  return (
    <header
      className="sticky top-0 z-20 flex items-center gap-3 px-4 py-3 border-b w-full"
      style={{ background: '#061674', borderColor: 'rgba(255,255,255,0.1)' }}
      role="banner"
    >
      {/* Logo */}
      <div className="h-10 px-2 cursor-pointer inline-flex items-center">
        <img
          src="/CuesHealth.png"
          alt="CuesHealth"
          className="h-full w-auto"
        />
      </div>

      {/* Search Bar */}
      <div
        ref={searchContainerRef}
        className="relative flex items-center gap-2 flex-1 max-w-[600px] min-w-0 h-10 rounded-[14px]"
        style={{
          background: 'rgba(255,255,255,0.15)',
          border: '1px solid rgba(255,255,255,0.2)',
        }}
        role="search"
      >
        <div className="flex items-center gap-2 flex-1 min-w-0 px-3">
          <Search className="w-[18px] h-[18px] text-white/80 flex-shrink-0" />
          <input
            type="search"
            placeholder="Search patients (Name, MRN, DOB, Phone)"
            className="flex-1 h-full border-0 outline-none bg-transparent text-white text-sm placeholder:text-white/60 min-w-0"
            value={searchQuery}
            onChange={handleSearchChange}
            onKeyDown={handleKeyDown}
            aria-label="Search patients"
            autoComplete="off"
          />
        </div>
        
        {/* Configure Button */}
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setShowConfigMenu(!showConfigMenu);
            setShowSearchResults(false);
          }}
          className="h-10 px-3 text-white/80 hover:text-white hover:bg-white/10 transition-colors flex-shrink-0 rounded-r-[14px]"
          aria-label="Configure search results"
          aria-expanded={showConfigMenu}
        >
          <Settings className="w-4 h-4" />
        </button>

        {/* Configuration Menu */}
        {showConfigMenu && (
          <div
            ref={configMenuRef}
            className="absolute top-[44px] right-0 w-64 bg-white border border-gray-200 rounded-xl shadow-lg z-50"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-3 space-y-3">
              <div>
                <div className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">
                  Result Fields
                </div>
                <div className="space-y-1">
                  <label className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-gray-50 cursor-pointer">
                    <Checkbox
                      checked={config.fields.showMRN}
                      onChange={(e) =>
                        updateConfig({
                          fields: { ...config.fields, showMRN: e.target.checked },
                        })
                      }
                    />
                    <span className="text-sm text-gray-700">MRN</span>
                  </label>
                  <label className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-gray-50 cursor-pointer">
                    <Checkbox
                      checked={config.fields.showDOB}
                      onChange={(e) =>
                        updateConfig({
                          fields: { ...config.fields, showDOB: e.target.checked },
                        })
                      }
                    />
                    <span className="text-sm text-gray-700">DOB</span>
                  </label>
                  <label className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-gray-50 cursor-pointer">
                    <Checkbox
                      checked={config.fields.showPhone}
                      onChange={(e) =>
                        updateConfig({
                          fields: { ...config.fields, showPhone: e.target.checked },
                        })
                      }
                    />
                    <span className="text-sm text-gray-700">Phone</span>
                  </label>
                </div>
              </div>
              <div className="border-t border-gray-200 pt-3">
                <div className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">
                  Actions
                </div>
                <div className="space-y-1">
                  <label className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-gray-50 cursor-pointer">
                    <Checkbox
                      checked={config.actions.view}
                      onChange={(e) =>
                        updateConfig({
                          actions: { ...config.actions, view: e.target.checked },
                        })
                      }
                    />
                    <span className="text-sm text-gray-700">View</span>
                  </label>
                  <label className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-gray-50 cursor-pointer">
                    <Checkbox
                      checked={config.actions.video}
                      onChange={(e) =>
                        updateConfig({
                          actions: { ...config.actions, video: e.target.checked },
                        })
                      }
                    />
                    <span className="text-sm text-gray-700">Video Visit</span>
                  </label>
                  <label className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-gray-50 cursor-pointer">
                    <Checkbox
                      checked={config.actions.message}
                      onChange={(e) =>
                        updateConfig({
                          actions: { ...config.actions, message: e.target.checked },
                        })
                      }
                    />
                    <span className="text-sm text-gray-700">Message Patient</span>
                  </label>
                  <label className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-gray-50 cursor-pointer">
                    <Checkbox
                      checked={config.actions.orderDevice}
                      onChange={(e) =>
                        updateConfig({
                          actions: { ...config.actions, orderDevice: e.target.checked },
                        })
                      }
                    />
                    <span className="text-sm text-gray-700">Order Device</span>
                  </label>
                  <label className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-gray-50 cursor-pointer">
                    <Checkbox
                      checked={config.actions.follow}
                      onChange={(e) =>
                        updateConfig({
                          actions: { ...config.actions, follow: e.target.checked },
                        })
                      }
                    />
                    <span className="text-sm text-gray-700">Follow</span>
                  </label>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Search Results */}
        {showSearchResults && (
          <div className="absolute top-[44px] left-0 right-0 bg-white border border-gray-200 rounded-xl shadow-lg z-50 max-h-[360px] overflow-y-auto">
            {isSearching ? (
              <div className="px-4 py-3 text-sm text-gray-500">Searching...</div>
            ) : results.length === 0 ? (
              <div className="px-4 py-3 text-sm text-gray-500">No matches found</div>
            ) : (
              <div className="py-2">
                {results.map((patient, index) => {
                  const name = `${patient.firstName || ''} ${patient.lastName || ''}`.trim() || 'Unnamed Patient';
                  const metaText = buildMetaText(patient);
                  const isActive = index === activeResultIndex;

                  return (
                    <div
                      key={patient.id}
                      className={cn(
                        'px-4 py-3 cursor-pointer transition-colors',
                        isActive ? 'bg-blue-50' : 'hover:bg-gray-50'
                      )}
                      onClick={() => handlePatientClick(patient)}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-gray-900">{name}</div>
                          {metaText && (
                            <div className="text-sm text-gray-600 mt-1">{metaText}</div>
                          )}
                        </div>
                        {enabledActions.length > 0 && (
                          <div className="flex items-center gap-1.5 flex-shrink-0">
                            {enabledActions.map((action) => (
                              <button
                                key={action.id}
                                type="button"
                                onClick={(e) => handleActionClick(e, action.id, patient)}
                                className="px-2.5 py-1 text-xs font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                              >
                                {action.label}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <button
        className="h-10 min-w-[40px] inline-flex items-center justify-center rounded-xl border transition-transform hover:-translate-y-0.5 active:translate-y-0.5"
        style={{
          background: 'rgba(255,255,255,0.1)',
          borderColor: 'rgba(255,255,255,0.2)',
          color: 'white',
        }}
        title="Add patient"
      >
        <span className="sr-only">Add Patient</span>
        <Plus className="w-[18px] h-[18px]" />
      </button>

      <button
        className="h-10 min-w-[40px] inline-flex items-center justify-center rounded-xl border transition-transform hover:-translate-y-0.5 active:translate-y-0.5 relative"
        style={{
          background: 'rgba(255,255,255,0.1)',
          borderColor: 'rgba(255,255,255,0.2)',
          color: 'white',
        }}
        onClick={() => setShowNotifications(!showNotifications)}
        title="Notifications"
      >
        <span className="sr-only">Notifications</span>
        <Bell className="w-5 h-5" />
        {showNotifications && (
          <div className="absolute top-[44px] right-0 w-80 bg-white border border-gray-200 rounded-xl shadow-lg p-4 z-50">
            <h3 className="font-semibold text-gray-900 mb-2">Notifications</h3>
            <div className="text-sm text-gray-500">No new notifications</div>
          </div>
        )}
      </button>

      <button
        className="h-10 min-w-[40px] inline-flex items-center justify-center rounded-xl border transition-transform hover:-translate-y-0.5 active:translate-y-0.5"
        style={{
          background: 'rgba(255,255,255,0.1)',
          borderColor: 'rgba(255,255,255,0.2)',
          color: 'white',
        }}
        title="Profile"
      >
        <span className="sr-only">Profile</span>
        <User className="w-5 h-5" />
      </button>

      {/* Follow Patient Modal */}
      {followPatientId && (
        <FollowPatientModal
          isOpen={isFollowModalOpen}
          onClose={() => {
            setIsFollowModalOpen(false);
            setFollowPatientId(null);
            setFollowPatientName('');
          }}
          patientId={followPatientId}
          patientName={followPatientName}
        />
      )}
    </header>
  );
};